<template>
  <div>
    <Head :title="$t('message.expense_requests.edit_expense_request')" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/expense/expenses">
        {{ $t('message.expense_requests.expenses', 2) }}
      </Link>
      <span class="text-indigo-400 font-medium">/</span> {{ expense.title }}
    </h1>
    <trashed-message v-if="expense.deleted_at" class="mb-6" @restore="restore"> This expenses has been deleted. </trashed-message>
    <div class="max-w-5xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input :disabled="expense.status == 'approved'" v-model="form.title" :error="form.errors.title" class="lg:w pb-8 pr-6 w-full" :label="$t('message.expense_requests.title')" />
          <!-- <label class="form-label">Items:</label> -->
          <div class="mb-6 mr-6 p-4 w-full bg-white rounded-md shadow overflow-x-auto">
            <table class="mb-6 w-full whitespace-nowrap">
              <thead>
                <tr class="text-left font-bold">
                  <th class="pb-4 pt-6 px-6">
                    {{ $t('message.expense_requests.name') }}
                  </th>
                  <th class="pb-4 pt-6 px-6">
                    {{ $t('message.expense_requests.description') }}
                  </th>
                  <th class="pb-4 pt-6 px-6">
                    {{ $t('message.expense_requests.cost') }}
                  </th>
                  <th class="pb-4 pt-6 px-6">
                    {{ $t('message.expense_requests.quantity') }}
                  </th>
                  <th class="pb-4 pt-6 px-6">
                    {{ $t('message.expense_requests.sub_total') }}
                  </th>
                  <th class="pb-4 pt-6 px-6" />
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in form.items" :key="item.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
                  <td class="border-t">
                    <div class="flex items-center px-6 py-4 focus:text-indigo-500" @click="expense.status == 'approved' && !canUpdateStatus ? null : openEditModal(index)">
                      {{ item.name }}
                    </div>
                  </td>
                  <td class="border-t">
                    <div class="flex items-center px-6 py-4 focus:text-indigo-500" @click="expense.status == 'approved' && !canUpdateStatus ? null : openEditModal(index)">
                      {{ item.description }}
                    </div>
                  </td>
                  <td class="border-t">
                    <div class="flex items-center px-6 py-4 focus:text-indigo-500" @click="expense.status == 'approved' && !canUpdateStatus ? null : openEditModal(index)">
                      {{ item.cost }}
                    </div>
                  </td>
                  <td class="border-t">
                    <div class="flex items-center px-6 py-4 focus:text-indigo-500" @click="expense.status == 'approved' && !canUpdateStatus ? null : openEditModal(index)">
                      {{ item.quantity }}
                    </div>
                  </td>
                  <td class="border-t">
                    <div class="flex items-center px-6 py-4 focus:text-indigo-500" @click="expense.status == 'approved' && !canUpdateStatus ? null : openEditModal(index)">
                      {{ formatPrice(item.sub_total) }}
                    </div>
                  </td>
                  <td class="w-px border-b border-t">
                    <button class="flex items-center px-4 focus:text-indigo-500" @click.prevent="expense.status == 'approved' ? null : removeRow(index)">
                      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </td>
                </tr>
                <tr v-if="form.items.length > 0" class="text-left font-bold">
                  <td class="pb-4 pt-6 px-6 border-t" colspan="4">{{ $t('message.expense_requests.total') }}:</td>
                  <td class="pb-4 pt-6 px-6 border-t">{{ formatPrice(form.total) }}</td>
                </tr>
              </tbody>
            </table>

            <button class="btn-indigo py-4 w-full" @click.prevent="openCreateModal">
              {{ $t('message.expense_requests.add_row') }}
            </button>
          </div>
          <select-input :disabled="expense.status == 'approved'" v-if="canUpdateStatus" v-model="form.status" :error="form.errors.status" class="lg:w pb-8 pr-6 w-full" :label="$t('message.general.status')">
            <option :value="null" />
            <option v-for="st in status" :key="st.id" :value="st.id">
              {{ $t(`message.expense_tickets.${slugifyStatus(st.name)}`) }}
            </option>
          </select-input>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button v-if="!expense.deleted_at && canDelete" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">
            {{ $t('message.expense_requests.delete_expense_request') }}
          </button>
          <loading-button v-if="canUpdate" :loading="form.processing" class="btn-indigo ml-auto" type="submit">
            {{ $t('message.expense_requests.update_expense_request') }}
          </loading-button>
        </div>
      </form>
    </div>
    <transition enter-active-class="transition ease-out duration-100" enter-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition ease-in duration-75" leave-class="opacity-100" leave-to-class="opacity-0">
      <div v-if="isModalOpen" class="fixed z-10 inset-0 overflow-y-auto">
        <div class="flex items-end justify-center pb-20 pt-4 px-4 min-h-screen text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" aria-hidden="true">
            <div class="absolute inset-0 bg-gray-500 opacity-75" />
          </div>
          <div class="inline-block align-bottom text-left bg-white rounded-lg shadow-xl overflow-hidden transform transition-all sm:align-middle sm:my-8 sm:w-full sm:max-w-lg">
            <div class="flex justify-end pt-4 px-4">
              <button class="text-gray-500 hover:text-gray-700 focus:text-gray-700 focus:outline-none" @click="closeModal">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="pb-4 pt-5 px-4 bg-white sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <div class="mt-2">
                    <form @submit.prevent="addRow">
                      <div class="flex flex-wrap -mb-4">
                        <text-input v-model="newItem.name" :error="newItem.errors.name" class="lg:w pb-8 pr-6 w-full" :label="$t('message.expense_requests.name')" />
                        <text-input v-model="newItem.description" :error="newItem.errors.description" class="lg:w pb-8 pr-6 w-full" :label="$t('message.expense_requests.description')" />
                        <text-input v-model="newItem.cost" type="number" :error="newItem.errors.cost" class="lg:w pb-8 pr-6 w-full" :label="$t('message.expense_requests.cost_per_item', { currency })" />
                        <text-input v-model="newItem.quantity" type="number" :error="newItem.errors.quantity" class="lg:w pb-8 pr-6 w-full" :label="$t('message.expense_requests.quantity')" />
                        <button class="btn-indigo mb-4 mr-6 px-6 w-full" @click.prevent="isEdit ? editRow() : addRow()">{{ isEdit ? $t('message.expense_requests.edit_item') : $t('message.expense_requests.add_item') }}</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput.vue'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import SelectInput from '@/Shared/SelectInput'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    TextareaInput,
    TextInput,
    TrashedMessage,
    SelectInput,
  },
  layout: Layout,
  props: {
    expense: Object,
    status: Array,
    canUpdate: Boolean,
    canUpdateStatus: Boolean,
    canDelete: Boolean,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        title: this.expense.title,
        items: this.expense.items.map((item) => {
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            cost: item.cost,
            quantity: item.quantity,
            sub_total: item.cost != null && item.quantity != null ? item.cost * item.quantity : null,
          }
        }),
        status: this.expense.status,
        total: this.expense.items.length > 0 ? this.expense.items.reduce((total, item) => total + item.cost * item.quantity, 0) : 0,
        requested_by: this.$page.props.auth.user.id,
      }),
      currency: this.$page.props.auth.user.account.currency,
      newItem: this.$inertia.form({
        name: null,
        description: null,
        cost: null,
        quantity: null,
        sub_total: null,
      }),
      isModalOpen: false,
      isEdit: false,
      currentIndex: null,
    }
  },
  methods: {
    openCreateModal() {
      if (this.expense.status == 'approved') {
        return
      }
      this.isEdit = false
      this.isModalOpen = true
    },

    openEditModal(index) {
      this.isEdit = true
      this.newItem.name = this.form.items[index].name
      this.newItem.description = this.form.items[index].description
      this.newItem.cost = this.form.items[index].cost
      this.newItem.quantity = this.form.items[index].quantity
      this.newItem.sub_total = this.form.items[index].sub_total
      this.currentIndex = index
      this.isModalOpen = true
    },

    closeModal() {
      this.isEdit = false
      this.newItem.name = null
      this.newItem.description = null
      this.newItem.cost = null
      this.newItem.quantity = null
      this.newItem.sub_total = null
      this.isModalOpen = false
    },

    addRow() {
      if (this.newItem.name == null) {
        this.newItem.errors.name = 'Name is required'
      }
      if (this.newItem.quantity == null) {
        this.newItem.errors.quantity = 'Quantity is required'
      }
      if (this.newItem.name != null && this.newItem.quantity != null) {
        if (this.newItem.cost != null && this.newItem.cost >= 0 && this.newItem.quantity != null && this.newItem.quantity >= 0) {
          this.newItem.sub_total = this.newItem.cost * this.newItem.quantity
        } else {
          this.newItem.sub_total = null
        }
        this.form.items.push({
          name: this.newItem.name,
          description: this.newItem.description,
          cost: this.newItem.cost,
          quantity: this.newItem.quantity,
          sub_total: this.newItem.sub_total,
        })

        this.newItem.name = null
        this.newItem.description = null
        this.newItem.cost = null
        this.newItem.quantity = null
        this.newItem.sub_total = null
        this.calculateTotal()
        this.closeModal()
      }
    },

    removeRow(index) {
      this.form.items.splice(index, 1)
      this.calculateTotal()
    },

    editRow() {
      if (this.newItem.name == null) {
        this.newItem.errors.name = 'Name is required'
      }
      if (this.newItem.quantity == null) {
        this.newItem.errors.quantity = 'Quantity is required'
      }
      if (this.newItem.name != null && this.newItem.quantity != null) {
        if (this.newItem.cost != null && this.newItem.cost >= 0 && this.newItem.quantity != null && this.newItem.quantity >= 0) {
          this.newItem.sub_total = this.newItem.cost * this.newItem.quantity
        } else {
          this.newItem.sub_total = null
        }
        var editedItem = {
          name: this.newItem.name,
          description: this.newItem.description,
          cost: this.newItem.cost,
          quantity: this.newItem.quantity,
          sub_total: this.newItem.sub_total,
        }
        let index = this.currentIndex
        this.form.items[index] = editedItem
      }

      this.newItem.name = null
      this.newItem.description = null
      this.newItem.cost = null
      this.newItem.quantity = null
      this.newItem.sub_total = null

      this.currentIndex = null

      this.calculateTotal()
      this.isEdit = false
      this.closeModal()
    },

    capitalisedFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },

    formatPrice(price) {
      const currency = this.$page.props.auth.user.account.currency
      return new Intl.NumberFormat('en-GM', { style: 'currency', currency }).format(price)
    },

    calculateTotal() {
      let total = 0
      for (let i = 0; i < this.form.items.length; i++) {
        if (this.form.items[i].sub_total != null) {
          total += this.form.items[i].sub_total
        } else {
          total += 0
        }
      }
      this.form.total = total
    },

    update() {
      this.form.put(`/expense/expenses/${this.expense.id}`)
    },

    destroy() {
      if (confirm('Are you sure you want to delete this expense?')) {
        this.$inertia.delete(`/expense/expenses/${this.expense.id}`)
      }
    },

    restore() {
      if (confirm('Are you sure you want to restore this expense?')) {
        this.$inertia.put(`/expense/expenses/${this.expense.id}/restore`)
      }
    },

    slugifyStatus(status) {
      return status.toLowerCase().replace(' ', '-')
    },
  },
}
</script>
